import { useEffect, useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { useAppStore } from '@/lib/app-store'
import { useBannerFreeRoute } from '@/hooks/use-banner-free-route'
import { OPEN_PREFERENCES_EVENT, readConsent, writeConsent, type ConsentCategories } from '@/lib/cookie-consent'
import { Button, ButtonLink } from '@/components/ui/button'
import { useModalLink, useModalMatch, useOpenModal } from '@/components/custom/modal-slot'

/** The consent copy the bar and the preferences dialog share. */
export function useCookieDescription() {
  const { t } = useLingui()
  return t`At D'Anchiano we use our own and third-party cookies to keep you signed in and remember your preferences, to measure how the site is used, and to show you interest-based advertising. Only the necessary ones are set unless you accept the rest: you can accept all cookies, reject the non-essential ones, or choose which to allow.`
}

export function CookieBanner() {
  const { t } = useLingui()
  const sdk = useAppStore((state) => state.store.sdk)
  // Whether the page being read is one the banner has to stay off; see `useBannerFreeRoute`. Derived from
  // the router on every render rather than latched, so leaving the page brings the banner straight back.
  const bannerFreeRoute = useBannerFreeRoute()

  // `decided` mirrors whether the visitor has made a choice (banner hidden once true). Seeded from
  // the cookie on mount — safe because the banner only renders client-side (wrapped in ClientOnly).
  const [decided, setDecided] = useState(() => readConsent().decided)
  const openModal = useOpenModal()
  const customizeLink = useModalLink('/@modal/cookies')
  // The preferences dialog commits the cookie itself; the bar learns about it when that route closes.
  const preferencesOpen = useModalMatch('/@modal/cookies') !== undefined
  const [wasPreferencesOpen, setWasPreferencesOpen] = useState(preferencesOpen)
  if (wasPreferencesOpen !== preferencesOpen) {
    setWasPreferencesOpen(preferencesOpen)
    if (!preferencesOpen) setDecided(readConsent().decided)
  }

  // Allow the dialog to be reopened from anywhere (e.g. the footer "Cookie settings" link) so users
  // can change or withdraw consent as easily as they gave it.
  useEffect(() => {
    const open = () => openModal('/@modal/cookies', undefined)
    window.addEventListener(OPEN_PREFERENCES_EVENT, open)
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, open)
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- subscribed once; `openModal` only reads the router at call time
  }, [])

  const commit = (categories: ConsentCategories) => {
    writeConsent(categories)
    setDecided(true)
  }

  const acceptAll = () => commit({ statistics: true, marketing: true })
  const rejectAll = () => commit({ statistics: false, marketing: false })

  const description = useCookieDescription()

  // The SDK embeds the app inside a customer's iframe — no banner there; consent is the host's job.
  if (sdk) return null

  return (
    <>
      {/* Deliberately only the bar. The preferences dialog below stays mounted and openable on a
          banner-free route, because the footer's "Cookie settings" is on those pages like everywhere
          else — and on the cookies policy in particular it is the control the text itself points at. */}
      {!decided && !bannerFreeRoute && (
        <div
          role="region"
          aria-label={t`Cookie consent`}
          className="cookie-banner bg-background fixed right-0 bottom-0 left-0 z-[1000] flex flex-col gap-4 p-4 shadow-[var(--footer-shadow)] sm:flex-row sm:items-center sm:justify-between print:hidden"
        >
          <div className="flex items-start gap-2.5">
            <span role="img" aria-label="cookie" className="text-2xl leading-none">
              🍪
            </span>
            <p className="text-sm">
              {description}{' '}
              <a href="/cookies-policy" target="_blank" rel="noopener noreferrer" className="underline">
                {t`Cookies policy`}
              </a>
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:shrink-0 sm:flex-row sm:items-center">
            <ButtonLink variant="ghost" {...customizeLink}>
              {t`Customize`}
            </ButtonLink>
            <Button type="button" variant="outline" onClick={rejectAll}>
              {t`Reject all`}
            </Button>
            <Button type="button" onClick={acceptAll}>
              {t`Accept all`}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
